import { FieldValues, UseFormReturn } from 'react-hook-form';
import {
    CheckboxElement,
    FormContainer,
    SelectElement,
    TextFieldElement,
} from 'react-hook-form-mui';
import { Stack } from '@mui/material';

import { EditButtons } from '@barboxx/admin-components';
import { useAuth } from '@barboxx/admin-utils';
import { Business } from '@barboxx/models';
import { Can, useBool } from '@barboxx/react-utils';
import {
    BOX_STATUS_TYPE,
    BUSINESS_PICKUP_MODE,
    HoursOfOperations,
} from '@barboxx/types';
import {
    enumToFormOptions,
    getMaxSalesCutoffBufferMinutes,
} from '@barboxx/utils';

// Generated by Copilot

export { BUSINESS_SCHEMA } from './BusinessForm.schema';

interface Props<T extends FieldValues = Business.UpdateRequest> {
    formContext: UseFormReturn<T>;
    onSubmit: (values: T) => boolean | void | Promise<boolean | void>;
    hoursOfOperations?: HoursOfOperations | null;
}

export function BusinessForm({
    formContext,
    onSubmit,
    hoursOfOperations,
}: Props<Business.UpdateRequest>) {
    const {
        auth: { user },
    } = useAuth();
    const isInternalUser = user.userType === 'Internal';
    const [editable, editableActions] = useBool(false);
    const lastCall = formContext.watch('lastCall') as string | undefined;
    const maxSalesCutoffBufferMinutes = getMaxSalesCutoffBufferMinutes(
        hoursOfOperations,
        lastCall
    );
    const salesCutoffBufferHelperText =
        typeof maxSalesCutoffBufferMinutes === 'number'
            ? `Max allowed for this schedule: ${maxSalesCutoffBufferMinutes} minutes.`
            : 'Buffer is capped by close time when schedule and last call are available.';

    const handleSubmit = async (values: Business.UpdateRequest) => {
        const shouldCloseEditMode = await onSubmit(values);

        if (shouldCloseEditMode !== false) {
            editableActions.off();
        }
    };

    const handleCancel = () => {
        editableActions.off();

        formContext.reset();
    };

    return (
        <FormContainer onSuccess={handleSubmit} formContext={formContext}>
            <Stack gap={2}>
                <SelectElement
                    name="boxStatus"
                    label="Box Status"
                    disabled={!editable}
                    options={enumToFormOptions(BOX_STATUS_TYPE)}
                    fullWidth
                />

                <CheckboxElement
                    label="Active Subscription"
                    name="active"
                    disabled={!editable}
                />
                <CheckboxElement
                    label="Pass Credit Card Processing Fees To Customer"
                    name="passCreditCardFeesToCustomer"
                    disabled={!editable}
                />
                {isInternalUser && (
                    <SelectElement
                        name="pickupMode"
                        label="Pickup Mode"
                        disabled={!editable}
                        options={enumToFormOptions(BUSINESS_PICKUP_MODE)}
                        fullWidth
                    />
                )}
                <Stack direction="row" gap={2} flexGrow={1}>
                    <TextFieldElement
                        name="taxRate"
                        label="Tax Rate"
                        type="number"
                        sx={{ flex: 1 }}
                        slotProps={{
                            input: {
                                readOnly: !editable,
                                inputMode: 'decimal',
                            },
                            htmlInput: {
                                step: 0.01,
                                min: 0,
                                max: 1,
                            },
                        }}
                    />
                    <Can I="update" a="Business">
                        <TextFieldElement
                            name="serviceFee"
                            sx={{ flex: 1 }}
                            slotProps={{
                                input: {
                                    readOnly: !editable,
                                    inputMode: 'decimal',
                                },
                                htmlInput: {
                                    step: 0.5,
                                    min: 0.99,
                                },
                            }}
                            label="Service Fee"
                            type="number"
                        />
                    </Can>
                </Stack>
                <Stack direction="row" gap={2}>
                    <TextFieldElement
                        name="maxDistance"
                        label="Max Distance"
                        type="number"
                        sx={{ flex: 1 }}
                        slotProps={{
                            input: {
                                readOnly: !editable,
                                inputMode: 'decimal',
                            },
                            htmlInput: {
                                step: 1,
                                min: 25,
                                max: 1000,
                            },
                        }}
                    />
                    <TextFieldElement
                        name="lastCall"
                        label="Last Call"
                        type="time"
                        sx={{ flex: 1 }}
                        slotProps={{
                            input: {
                                readOnly: !editable,
                                inputMode: 'numeric',
                            },
                            htmlInput: {
                                step: 1,
                            },
                        }}
                    />
                    <TextFieldElement
                        name="orderSalesCutoffBufferMinutes"
                        label="Sales Cutoff Buffer (Minutes)"
                        type="number"
                        helperText={salesCutoffBufferHelperText}
                        sx={{ flex: 1 }}
                        slotProps={{
                            input: {
                                readOnly: !editable,
                                inputMode: 'numeric',
                            },
                            htmlInput: {
                                step: 1,
                                min: 0,
                            },
                        }}
                    />
                </Stack>
                <Stack direction="row" gap={2}>
                    <TextFieldElement
                        name="orderPickupReminderCount"
                        label="Pickup Reminder Count"
                        type="number"
                        sx={{ flex: 1 }}
                        slotProps={{
                            input: {
                                readOnly: !editable,
                                inputMode: 'numeric',
                            },
                            htmlInput: {
                                step: 1,
                                min: 1,
                            },
                        }}
                    />
                    <TextFieldElement
                        name="orderPickupTrashDelayMinutes"
                        label="Trash Delay (Minutes)"
                        type="number"
                        sx={{ flex: 1 }}
                        slotProps={{
                            input: {
                                readOnly: !editable,
                                inputMode: 'numeric',
                            },
                            htmlInput: {
                                step: 1,
                                min: 1,
                            },
                        }}
                    />
                    <TextFieldElement
                        name="orderPickupPostReminderGraceMinutes"
                        label="Post-Reminder Grace (Minutes)"
                        type="number"
                        sx={{ flex: 1 }}
                        slotProps={{
                            input: {
                                readOnly: !editable,
                                inputMode: 'numeric',
                            },
                            htmlInput: {
                                step: 1,
                                min: 1,
                            },
                        }}
                    />
                </Stack>
                <Stack direction="row" gap={2}>
                    <TextFieldElement
                        name="orderPickupReminderCount"
                        label="Pickup Reminder Count"
                        type="number"
                        sx={{ flex: 1 }}
                        slotProps={{
                            input: {
                                readOnly: !editable,
                                inputMode: 'numeric',
                            },
                            htmlInput: {
                                step: 1,
                                min: 1,
                            },
                        }}
                    />
                    <TextFieldElement
                        name="orderPickupTrashDelayMinutes"
                        label="Trash Delay (Minutes)"
                        type="number"
                        sx={{ flex: 1 }}
                        slotProps={{
                            input: {
                                readOnly: !editable,
                                inputMode: 'numeric',
                            },
                            htmlInput: {
                                step: 1,
                                min: 1,
                            },
                        }}
                    />
                    <TextFieldElement
                        name="orderPickupPostReminderGraceMinutes"
                        label="Post-Reminder Grace (Minutes)"
                        type="number"
                        sx={{ flex: 1 }}
                        slotProps={{
                            input: {
                                readOnly: !editable,
                                inputMode: 'numeric',
                            },
                            htmlInput: {
                                step: 1,
                                min: 1,
                            },
                        }}
                    />
                </Stack>
                <Stack direction="row" gap={2}>
                    <TextFieldElement
                        name="maxDrinksPerOrder"
                        label="Max Drinks Per Order"
                        type="number"
                        sx={{ flex: 1 }}
                        slotProps={{
                            input: {
                                readOnly: !editable,
                                inputMode: 'numeric',
                            },
                            htmlInput: {
                                step: 1,
                                min: 1,
                            },
                        }}
                    />
                    <TextFieldElement
                        name="maxOrdersInQueue"
                        label="Max Orders In Queue"
                        type="number"
                        sx={{ flex: 1 }}
                        slotProps={{
                            input: {
                                readOnly: !editable,
                                inputMode: 'numeric',
                            },
                            htmlInput: {
                                step: 1,
                                min: 1,
                            },
                        }}
                    />
                </Stack>
                <Can I="update" a="Business">
                    <EditButtons
                        formContext={formContext}
                        handleCancel={handleCancel}
                        editable={editable}
                        editableActions={editableActions}
                    />
                </Can>
            </Stack>
        </FormContainer>
    );
}
